import { useGetConversation } from '@web/conversations/hooks/useGetConversation';
import { useGetMessages } from '@web/conversations/hooks/useGetMessages';
import { useMessagesRefetchCounter } from '@web/conversations/hooks/useMessagesRefetchCounter';
import MessageEditModal from '@web/conversations/Message/MessageEditModal/MessageEditModal';
import { ConversationMainSection } from '@web/conversations/Conversation/ConversationMainSection/ConversationMainSection';
import { DraftMessagesProvider } from '@web/conversations/DraftMessagesProvider/DraftMessagesProvider';

import {
  ConversationLayout,
  ConversationLayoutFooter,
  ConversationLayoutHeader,
  ConversationLayoutMain,
  ConversationLayoutProvider,
  ConversationLayoutSidebar,
} from './ConversationLayout/ConversationLayout';
import { ConversationHeader } from './ConversationHeader/ConversationHeader';
import { ConversationMembers } from './ConversationMembers/ConversationMembers';
import { ConversationProvider } from './ConversationProvider/ConversationProvider';
import { ConversationSkeleton } from './ConversationSkeleton/ConversationSkeleton';
import { ConversationListeners } from './ConversationListeners/ConversationListeners';
import { ConversationTypingNotifier } from './ConversationTypingNotifier/ConversationTypingNotifier';
import { ConversationTextEditor } from './ConversationTextEditor/ConversationTextEditor';
import { QuotedMessagePreview } from './QuotedMessagePreview/QuotedMessagePreview';
import { Props } from './conversation.types';

export const Conversation = ({ id, selectedMessageId }: Props) => {
  const {
    data: conversation,
    isLoading,
    isPlaceholderData,
  } = useGetConversation({
    id,
  });
  const {
    data: messagesData,
    isFetching: isFetchingMessages,
    isLoading: isLoadingMessages,
  } = useGetMessages({
    conversationId: id,
    selectedMessageId,
  });
  const messagesRefetchCounter = useMessagesRefetchCounter(isFetchingMessages);

  const isConversationReady = !(isLoading || isLoadingMessages || isPlaceholderData);

  if (!isConversationReady) {
    return <ConversationSkeleton name={conversation.name} />;
  }
  const messages = messagesData.pages.flat();

  return (
    <DraftMessagesProvider>
      <ConversationProvider
        conversation={conversation}
        messages={messages}
        messagesRefetchCounter={messagesRefetchCounter}
        selectedMessageId={selectedMessageId}>
        <ConversationLayoutProvider>
          <MessageEditModal />

          <ConversationLayout>
            <ConversationLayoutHeader>
              <ConversationHeader />
            </ConversationLayoutHeader>

            <ConversationLayoutMain>
              <ConversationMainSection />
            </ConversationLayoutMain>

            <ConversationLayoutSidebar>
              <ConversationMembers />
            </ConversationLayoutSidebar>

            <ConversationLayoutFooter>
              <ConversationTypingNotifier />
              <QuotedMessagePreview />
              <ConversationTextEditor />
            </ConversationLayoutFooter>
          </ConversationLayout>
        </ConversationLayoutProvider>

        <ConversationListeners />
      </ConversationProvider>
    </DraftMessagesProvider>
  );
};
